import type { ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { FadingRuler } from "../shared/FadingRuler";
import { NothingFuse } from "../shared/NothingFuse";
import type { RiskSignal } from "./risk-signal-card-utils";
import { formatAge } from "./risk-signal-card-utils";
import { RiskSignalExpandedDetails } from "./RiskSignalExpandedDetails";
import type { RiskSignalDrift } from "./useRiskSignalDrift";
import { formatDriftLabel, inferSignalDirection } from "./useRiskSignalDrift";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface RiskSignalCardRowProps {
  signal: RiskSignal;
  index: number;
  expanded: boolean;
  freshnessStale: boolean;
  drift?: RiskSignalDrift;
  padding: string;
  metaSize: string;
  microSize: string;
  textSize: string;
  titleSize: string;
  displayedAnalysis: string;
  compact?: boolean;
  onToggle: () => void;
}

export function RiskSignalCardRow({
  signal,
  index,
  expanded,
  freshnessStale,
  drift,
  padding,
  metaSize,
  microSize,
  textSize,
  titleSize,
  displayedAnalysis,
  compact,
  onToggle,
}: RiskSignalCardRowProps) {
  const direction = inferSignalDirection(signal);
  const driftLabel = formatDriftLabel(drift?.label);
  const directionColor =
    direction === "BULLISH"
      ? "var(--fintheon-bullish)"
      : direction === "BEARISH"
        ? "var(--fintheon-bearish)"
        : "var(--fintheon-muted)";

  return (
    <div className="min-w-0">
      {index > 0 && <FadingRuler className="my-1" />}
      <button
        type="button"
        onClick={onToggle}
        className={`w-full min-w-0 cursor-pointer rounded-md text-left transition-colors hover:text-[var(--fintheon-accent)] ${
          expanded ? "text-[var(--fintheon-accent)]" : ""
        } ${freshnessStale ? "opacity-70" : ""} ${compact ? "min-h-[72px]" : ""} ${padding}`}
      >
        <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_5.25rem] items-center gap-2 max-[767px]:grid-cols-[auto_minmax(0,1fr)_6.35rem] max-[767px]:gap-2.5">
          {expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-[var(--fintheon-muted)]/45" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-[var(--fintheon-muted)]/45" />
          )}
          <span
            className={`${titleSize} min-w-0 flex-1 font-semibold text-[var(--fintheon-text)] line-clamp-1`}
          >
            {signal.title}
          </span>
          <span className="flex min-w-0 items-center justify-end gap-1.5 text-right font-mono leading-tight max-[767px]:gap-2">
            <ChevronRight
              className="h-3 w-3 shrink-0"
              style={{
                color: directionColor,
                transform:
                  direction === "BULLISH"
                    ? "rotate(-90deg)"
                    : direction === "BEARISH"
                      ? "rotate(90deg)"
                      : "rotate(0deg)",
              }}
            />
            <span className="block w-[4.35rem] shrink-0 max-[767px]:w-[5.2rem]">
              <span
                className={`mb-1 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1 ${metaSize} text-[var(--fintheon-muted)]/50`}
              >
                <span className="min-w-0 truncate whitespace-nowrap">
                  {driftLabel}
                </span>
                <span className="whitespace-nowrap tabular-nums">
                  <FintheonNumericText text={String(signal.score.toFixed(1))} />
                </span>
              </span>
              <NothingFuse
                value={Math.max(0, Math.min(1, signal.score / 10))}
                score={signal.score}
                thickness={2}
                segments={5}
              />
            </span>
          </span>
        </div>
      </button>
      <ExpandedShell expanded={expanded}>
        <RiskSignalExpandedDetails
          signal={signal}
          displayedAnalysis={displayedAnalysis}
          drift={drift}
          microSize={microSize}
          metaSize={metaSize}
          textSize={textSize}
          compact={compact}
        />
        {!compact && (
          <RelatedContext
            signal={signal}
            microSize={microSize}
            textSize={textSize}
          />
        )}
      </ExpandedShell>
    </div>
  );
}

function ExpandedShell({
  expanded,
  children,
}: {
  expanded: boolean;
  children: ReactNode;
}) {
  return (
    <div
      aria-hidden={!expanded}
      className={`grid transition-[grid-template-rows,opacity,transform,filter] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
        expanded
          ? "translate-y-0 opacity-100 blur-none"
          : "pointer-events-none -translate-y-1 opacity-0 blur-[1px]"
      }`}
      style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

function RelatedContext({
  signal,
  microSize,
  textSize,
}: {
  signal: RiskSignal;
  microSize: string;
  textSize: string;
}) {
  return (
    <div className="ml-5 space-y-2 px-2 pb-2 pr-16 max-[767px]:ml-4 max-[767px]:pr-3">
      {signal.relatedHeadlines.length > 0 && (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span
              className={`${microSize} text-[var(--fintheon-muted)]/30 uppercase tracking-wider`}
            >
              Related Headlines
            </span>
            <span className={`${microSize} text-[var(--fintheon-muted)]/30`}>
              <FintheonNumericText
                text={formatAge(signal.generatedAt)}
                kind="date-time"
              />
            </span>
          </div>
          {signal.relatedHeadlines.map((headline) => (
            <div
              key={headline}
              className={`${textSize} text-[var(--fintheon-muted)]/50 line-clamp-1 max-[767px]:text-[var(--fintheon-muted)]/62`}
            >
              {headline}
            </div>
          ))}
        </div>
      )}
      {signal.narrativeThreads.length > 0 && (
        <div className="flex flex-wrap gap-x-2 gap-y-1">
          {signal.narrativeThreads.map((thread) => (
            <span
              key={thread}
              className={`${microSize} text-[var(--fintheon-accent)]/70`}
            >
              {thread}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
